/**
 * `calendar` — monday's `CalendarBoardView` (`specs/M4-V4-calendar.md`).
 *
 * Four of the account's five calendar views store `{}`, so the defaults here are not a
 * convenience: they are what four real views render with. An unconfigured calendar places
 * items by **every** date and timeline column on the board.
 */
import { z } from 'zod';
import { defineViewType, normaliseOverDefaults } from '../types.js';
import { mondayShapedSettings } from '../from-monday.js';
import { columnSelectionSchema, EMPTY_COLUMN_SELECTION, isEmptySelection, selectedColumnIds, } from '../column-selection.js';
// The kind's own monday reader, so `normaliseSettings` can recognise an imported blob
// (P-025). Re-exported below as well, which is how every caller outside has always had it.
import { calendarSettingsFromMonday } from './from-monday.js';
export * from './from-monday.js';
export * from './to-monday.js';
export * from './events.js';
export const CALENDAR_MONDAY_KEYS = new Set([
    'columns',
    'color_by_id',
    'show_time_slots',
    'display_legend',
]);
export const calendarViewSettingsSchema = z.object({
    dateColumns: columnSelectionSchema,
    colorColumnId: z.string().nullable(),
    showTimeSlots: z.boolean(),
    displayLegend: z.boolean(),
    range: z.enum(['month', 'week', 'day']),
    weekStartsOn: z.union([z.literal(0), z.literal(1)]),
    mondayKeys: z.array(z.string()),
    mondayExtras: z.record(z.string(), z.unknown()),
});
export const DEFAULT_CALENDAR_VIEW_SETTINGS = {
    dateColumns: EMPTY_COLUMN_SELECTION,
    colorColumnId: null,
    showTimeSlots: false,
    displayLegend: true,
    range: 'month',
    weekStartsOn: 1,
    mondayKeys: [],
    mondayExtras: {},
};
/** The column types that can place an item on a day. */
export const CALENDAR_DATE_COLUMN_TYPES = ['date', 'timeline'];
/**
 * The columns that place an item: the configured ones, and **every** date/timeline column
 * when nothing is configured (four of five production rows store `{}`, `[V4§2.4]`).
 */
export function calendarDateColumnIds(settings, columns) {
    const placeable = columns.filter((column) => CALENDAR_DATE_COLUMN_TYPES.includes(column.type));
    if (isEmptySelection(settings.dateColumns))
        return placeable.map((column) => column.id);
    const chosen = new Set(selectedColumnIds(settings.dateColumns, columns.map((column) => column.id)));
    return placeable.filter((column) => chosen.has(column.id)).map((column) => column.id);
}
/** Measured on the sandbox (`[V4§2.1]`). */
export const CALENDAR_METRICS = {
    weekdayRowHeight: 33,
    dateCellHeight: 23,
    eventHeight: 21,
    eventPitch: 22,
    eventRadius: 5,
    showMoreHeight: 15,
    legendHeight: 53,
    gridBorder: '#d0d4e4',
};
/** How many pills fit in a day cell before `+N more` takes the last line. */
export function eventsPerDayCell(cellHeight) {
    const usable = cellHeight - CALENDAR_METRICS.dateCellHeight - CALENDAR_METRICS.showMoreHeight;
    return Math.max(0, Math.floor(usable / CALENDAR_METRICS.eventPitch));
}
export const calendarView = defineViewType({
    id: 'calendar',
    title: 'Calendar',
    mondayViewType: 'CalendarBoardView',
    kind: 'APP',
    settingsSchema: calendarViewSettingsSchema,
    defaultSettings: DEFAULT_CALENDAR_VIEW_SETTINGS,
    normaliseSettings: (input) => normaliseOverDefaults('calendar', calendarViewSettingsSchema, DEFAULT_CALENDAR_VIEW_SETTINGS, mondayShapedSettings(input, DEFAULT_CALENDAR_VIEW_SETTINGS, calendarSettingsFromMonday)),
    requiredColumnTypes: [],
    supportsFilters: true,
    supportsSort: true,
    supportsGroupBy: false,
    canBeDefault: true,
    implemented: true,
    plannedIn: null,
    gallery: { order: 4, section: 'board-views' },
});
//# sourceMappingURL=index.js.map